import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { IPreScreenModalProps } from "tsi-common-react/src/apps/checkout/BillingAddress/index";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/components/Link/index";
import { PreQualCustomerResponse } from "tsi-common-react/src/models/financing";
import { isoAPR, isoMonths } from "tsi-common-react/src/models/nominals";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { focusElement } from "tsi-common-react/src/utils/keyboardFocus";
import { getPageSetting } from "tsi-common-react/src/utils/settings";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { jt, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import tempurCreditCard from "../../../assets/img/checkout/tempur-credit-card.png";
import tempurPreApprovalCouple from "../../../assets/img/checkout/tempur-pre-approval-couple.jpg";
import iconInstantUse from "../../../assets/svg/instant-use.svg";
import iconNoAnnualFee from "../../../assets/svg/no-annual-fee.svg";
import iconXClose from "../../../assets/svg/x-close.svg";

const MODAL_STYLE = {
    content: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        boxShadow: "none",
    },
};

const getBestFinancingPlan = () => {
    const plans = listFinancingPlans();
    return plans.length > 0 ? plans[plans.length - 1] : null;
};

const getFinancingPlanDetails = () => {
    const bestPlan = getBestFinancingPlan();
    if (!bestPlan) {
        return { planAPR: 0, planLength: 0 };
    }
    return {
        planAPR: parseFloat(isoAPR.unwrap(bestPlan.apr)),
        planLength: isoMonths.unwrap(bestPlan.length),
    };
};

const getContinueButtonClass = () =>
    classNames({
        "button": true,
        "checkout-step-pre-approval__button": true,
        "checkout-step-pre-approval__button--continue": true,
        "al-checkout-pre-approval__button--continue": true,
    });

const getRejectButtonClass = () =>
    classNames({
        "button": true,
        "button--inverse": true,
        "checkout-step-pre-approval__button": true,
        "checkout-step-pre-approval__button--cancel": true,
        "al-checkout-pre-approval__button--cancel": true,
    });

const handleModalAfterOpen = () => {
    focusElement(".checkout-step-pre-approval__close");
};

export const PreScreenModal = (
    props: IPreScreenModalProps,
): React.ReactNode => {
    const financeLink = usePageURL("finance_link");

    const handleClose = (
        e: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        e.preventDefault();
        props.onRequestClose(PreQualCustomerResponse.CLOSE);
    };

    const handleAccept = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        props.onRequestClose(PreQualCustomerResponse.ACCEPT);
    };

    const handleReject = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        props.onRequestClose(PreQualCustomerResponse.REJECT);
    };

    const { planAPR, planLength } = getFinancingPlanDetails();
    const bgImgStyle: React.CSSProperties = {
        backgroundImage: `url('${tempurPreApprovalCouple}')`,
    };
    const continueClass = getContinueButtonClass();
    const rejectClass = getRejectButtonClass();

    return (
        <Modal
            aria={{ modal: true }}
            contentLabel={t`Pre-Approval`}
            style={MODAL_STYLE}
            className="checkout-step-pre-approval"
            isOpen={props.isOpen}
            onAfterOpen={handleModalAfterOpen}
            onRequestClose={handleClose}
            role="dialog"
        >
            <div className="checkout-step-pre-approval__header">
                <button
                    aria-label={t`Close`}
                    className="checkout-step-pre-approval__close al-checkout-pre-approval__button--close"
                    onClick={handleClose}
                >
                    <SVG
                        aria-hidden="true"
                        src={iconXClose}
                        title={t`Close Icon`}
                    />
                </button>
                <h1 className="checkout-step-pre-approval__header-title">
                    {richT(
                        t`The Tempur-Pedic<superscript>®</superscript> Credit Card`,
                        {
                            superscript: (children) => (
                                <span className="checkout-step-pre-approval__header-title--r">
                                    <sup>{children}</sup>
                                </span>
                            ),
                        },
                    )}
                </h1>
            </div>
            <div className="checkout-step-pre-approval__body">
                <div className="checkout-step-pre-approval__title-container">
                    <div className="checkout-step-pre-approval__title-image-container">
                        <img
                            src={tempurCreditCard}
                            className="checkout-step-pre-approval__title-image"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    <h2 className="checkout-step-pre-approval__title">
                        {(() => {
                            const asterisk = <sup key="ast">*</sup>;
                            const regMark = <sup key="reg">®</sup>;
                            const preQualified = (
                                <span
                                    key="prequalified"
                                    className="checkout-step-pre-approval__title--bold"
                                >
                                    {jt`pre-qualified${asterisk}`}
                                </span>
                            );
                            const forAccount = (
                                <div
                                    key="account"
                                    className="checkout-step-pre-approval__pre-title"
                                >
                                    {jt`for a Tempur-Pedic${regMark} Credit Card account.`}
                                </div>
                            );
                            return jt`You're ${preQualified} ${forAccount}`;
                        })()}
                    </h2>
                </div>
                <div className="checkout-step-pre-approval__content checkout-step-pre-approval__content--left">
                    <div className="checkout-step-pre-approval__data-box checkout-step-pre-approval__data-box--limit">
                        {(() => {
                            const asterisk = <sup key="ast">*</sup>;
                            const amount = (
                                <span
                                    key="amount"
                                    className="checkout-step-pre-approval__data-box-data"
                                    style={{ display: "block" }}
                                >
                                    {`$${props.preApprovalLimit}`}
                                </span>
                            );
                            return (
                                <span className="checkout-step-pre-approval__data-box-title">
                                    {jt`pre-qualified${asterisk} credit limit up to ${amount}`}
                                </span>
                            );
                        })()}
                    </div>
                    <div className="checkout-step-pre-approval__data-box checkout-step-pre-approval__data-box--offer">
                        <div className="checkout-step-pre-approval__data-box-title">
                            {t`special financing offer`}
                        </div>
                        <div className="checkout-step-pre-approval__data-box-data">
                            {interpolate(t`%s% APR for %s months`, [
                                planAPR,
                                planLength,
                            ])}
                            <sup>
                                <Link href={financeLink}>1</Link>
                            </sup>
                        </div>
                    </div>
                    <div className="checkout-step-pre-approval__value-props">
                        <div className="checkout-step-pre-approval__value-prop">
                            <SVG
                                className="checkout-step-pre-approval__value-icon checkout-step-pre-approval__value-icon--fee"
                                title={t`Pre-Approved Icon`}
                                src={iconNoAnnualFee}
                            />
                            {(() => {
                                const ref = <sup key="ref">1</sup>;
                                return jt`No Annual Fee${ref}`;
                            })()}
                        </div>
                        <div className="checkout-step-pre-approval__value-prop">
                            <SVG
                                className="checkout-step-pre-approval__value-icon checkout-step-pre-approval__value-icon--instant"
                                title={t`Use Now Icon`}
                                src={iconInstantUse}
                            />
                            {t`Use instantly on Tempurpedic.com`}
                        </div>
                    </div>
                    <div className="checkout-step-pre-approval__opt-out">
                        {(() => {
                            const notice = (
                                <span key="notice">{t`PRESCREEN & OPT-OUT NOTICE`}</span>
                            );
                            return jt`You can choose to stop receiving "prescreened" offers of credit from this and other companies by calling toll-free 1-888-567-8688. See the ${notice} by selecting "Continue to Application" for more information about prescreened offers.`;
                        })()}
                    </div>
                </div>
                <div className="checkout-step-pre-approval__content checkout-step-pre-approval__content--right">
                    <div
                        className="checkout-step-pre-approval__image-container"
                        style={bgImgStyle}
                    ></div>
                    <div className="checkout-step-pre-approval__buttons">
                        <FinancingModalTrigger
                            modalType="full-app"
                            className={continueClass}
                            onClick={handleAccept}
                        >
                            {t`Continue to Application`}
                        </FinancingModalTrigger>
                        <button className={rejectClass} onClick={handleReject}>
                            {t`Not now`}
                        </button>
                    </div>
                </div>
                <div
                    className="checkout-step-pre-approval__small-print"
                    dangerouslySetInnerHTML={{
                        __html:
                            getPageSetting("pre_approval_modal_fine_print") ||
                            "",
                    }}
                ></div>
                <div className="checkout-step-pre-approval__opt-out-fine-print">
                    {(() => {
                        const noticeTitle = (
                            <span key="title">{t`PRESCREEN & OPT-OUT NOTICE:`}</span>
                        );
                        return jt`${noticeTitle} This "prescreened" offer of credit is based on information in your credit report indicating that you meet certain criteria. This offer is not guaranteed if you do not meet our criteria. If you do not want to receive prescreened offers of credit from this and other companies, call the consumer reporting agencies toll-free at 1-888-567-8688, or write: Equifax, Inc., P.O. Box 740123, Atlanta, GA 30374-0123; TransUnion, P.O. Box 505, Woodlyn, PA 19094-0505; Experian, P.O. Box 919, Allen, TX 75013.`;
                    })()}
                </div>
            </div>
        </Modal>
    );
};
